import { useState } from 'react'
import { api } from '../api/client'
import type { PassiveInventory } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'
import { Card, ErrorBanner, Spinner } from './common'

/**
 * Passive inventory settings: which recognised devices become CIs without
 * being contacted (medical devices must not be scanned). The pass runs every
 * hour from the Control Plane; «Run now» forces it. See
 * `passive_inventory_service`.
 */
export function InventarioPassivo() {
  const { principal } = useAuth()
  const admin = Boolean(principal?.roles.includes('tenant_admin') || principal?.roles.includes('provider_admin'))
  const data = useAsync(() => api.passiveInventory(), [])
  const [draft, setDraft] = useState<{ mode: PassiveInventory['mode']; days: number } | null>(null)
  const [busy, setBusy] = useState(false)
  const [outcome, setOutcome] = useState<string | null>(null)
  const [error, setError] = useState<unknown>(null)
  const d = data.data
  const mode = draft?.mode ?? d?.mode ?? 'medical'
  const days = draft?.days ?? d?.max_age_days ?? 14

  async function save() {
    setBusy(true)
    setError(null)
    try {
      await api.savePassiveInventory(mode, days)
      setDraft(null)
      data.reload()
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  async function run() {
    setBusy(true)
    setError(null)
    try {
      const r = await api.runPassiveInventory()
      setOutcome(t('{p} dispositivi registrati o aggiornati come CI su {c} idonei; {k} già nel CMDB da una scansione, lasciati com’erano.', { p: r.promoted, c: r.candidates, k: r.skipped_known })
        + (r.from_wire ? ' ' + t('{w} di questi sono noti solo per indirizzo.', { w: r.from_wire }) : ''))
      data.reload()
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title={t('Inventario passivo')}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Rileva e registra come CI i dispositivi riconosciuti passivamente, senza contattarli o sottoporli a scansione. Utilizza le informazioni raccolte da switch, traffico di rete, DHCP, firewall e identificazione del prodotto per determinarne costruttore, modello, IP, VLAN, switch e porta. Il rilevamento viene aggiornato ogni ora o su richiesta con «Esegui adesso». I CI già presenti da una scansione non vengono modificati; quelli non più rilevati seguono il normale ciclo di vita dei CI.')}
      </p>
      <p className="field__hint">
        {t('Anche i sistemi che parlano DICOM o HL7 visti solo per indirizzo, dietro un router (PACS, RIS, motori HL7, analizzatori di laboratorio): un apparecchio riconosciuto dal prodotto diventa elettromedicale, gli altri server o postazione secondo il sistema che hanno dichiarato sul filo. I PC d’ufficio visti solo per indirizzo non si registrano.')}
      </p>
      <ErrorBanner error={data.error} />
      <ErrorBanner error={error} />
      {data.loading && !d && <Spinner />}
      {d && (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="field">
              <label htmlFor="passive-mode">{t('Cosa registrare')}</label>
              <select id="passive-mode" value={mode} disabled={!admin}
                      onChange={(e) => setDraft({ mode: e.target.value as PassiveInventory['mode'], days })}>
                <option value="medical">{t('Solo gli elettromedicali')}</option>
                <option value="recognised">{t('Tutti i dispositivi riconosciuti')}</option>
                <option value="off">{t('Spento')}</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="passive-days">{t('Visti negli ultimi giorni')}</label>
              <input id="passive-days" type="number" min={1} max={365} value={days} disabled={!admin}
                     onChange={(e) => setDraft({ mode, days: Number(e.target.value) || 1 })} style={{ width: 90 }} />
            </div>
            {admin && draft && <button type="button" onClick={() => void save()} disabled={busy}>{t('Salva')}</button>}
            {admin && <button type="button" className="secondary" onClick={() => void run()} disabled={busy || mode === 'off'}>{t('Esegui adesso')}</button>}
          </div>
          {outcome && <p className="field__hint">{outcome}</p>}
          {d.last_run_at && d.last_result && (
            <p className="field__hint">
              {t('Ultimo giro: {q} · {p} registrati o aggiornati, {c} idonei, {k} già nel CMDB.', {
                q: new Date(d.last_run_at).toLocaleString(localeCorrente()),
                p: d.last_result.promoted, c: d.last_result.candidates, k: d.last_result.skipped_known,
              })}
              {d.last_result.from_wire ? ' ' + t('{w} di questi sono noti solo per indirizzo.', { w: d.last_result.from_wire }) : ''}
            </p>
          )}
        </>
      )}
    </Card>
  )
}
